import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, AudioWaveform } from "lucide-react";
import { track, EVENTS } from "../lib/analytics";

const AUDIO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/hero/sample-audio`;

export function SampleCallPlayer() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const audio = new Audio(AUDIO_URL);
    audio.preload = "none"; // don't download until click — lightweight landing
    audioRef.current = audio;

    const onLoaded = () => { setDuration(audio.duration || 0); setReady(true); };
    const onTime = () => {
      setCurrent(audio.currentTime || 0);
      setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
    };
    const onEnd = () => { setPlaying(false); setProgress(0); setCurrent(0); audio.currentTime = 0; };
    const onErr = () => { setReady(false); setPlaying(false); };

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("error", onErr);
    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("error", onErr);
      audio.pause();
      audio.src = "";
    };
  }, []);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
      return;
    }
    try {
      await a.play();
      setPlaying(true);
      track(EVENTS.AUDIO_SAMPLE_PLAYED, { location: "hero" });
    } catch {
      setReady(false);
    }
  };

  const fmt = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  return (
    <div
      className="inline-flex items-center gap-3 bg-white/90 backdrop-blur rounded-full pl-2 pr-4 py-2 border border-border/60 soft-shadow hover:shadow-md transition-shadow max-w-full"
      data-testid="hero-sample-player"
    >
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause sample" : "Play CherishLink sample call"}
        data-testid="hero-sample-play"
        className="h-10 w-10 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground grid place-items-center transition-colors shrink-0"
      >
        {playing ? <Pause className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 fill-current translate-x-0.5" />}
      </button>
      <div className="flex flex-col min-w-0 flex-1 sm:min-w-[180px]">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground truncate">
          <AudioWaveform className={`h-3.5 w-3.5 shrink-0 ${playing ? "text-secondary animate-pulse" : "text-muted-foreground"}`} />
          <span className="truncate">Hear a sample CherishLink check-in</span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <div className="relative h-1 flex-1 bg-muted rounded-full overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-primary rounded-full transition-all duration-150"
              style={{ width: `${Math.min(100, progress * 100)}%` }}
            />
          </div>
          <span className="text-[11px] text-muted-foreground tabular-nums font-medium">
            {fmt(playing || current > 0 ? current : duration || 13)}
          </span>
        </div>
      </div>
    </div>
  );
}
